// TaskCard — MD3 Card с title/description/status/priority/dueAt.
// Используется в TasksListPage и TaskDetailPage.
import { Card, CardContent, Typography, Stack, Box } from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { Task } from '@/api/tasks';
import { TaskStatusChip } from './TaskStatusChip';
import { TaskPriorityChip } from './TaskPriorityChip';

interface TaskCardProps {
  /** DTO задачи. */
  task: Task;
  /** Callback при клике на карточку. */
  onClick?: (task: Task) => void;
}

/** MD3 Card для отображения задачи в списке/детали. */
export function TaskCard({ task, onClick }: TaskCardProps): JSX.Element {
  const handleClick = (): void => {
    onClick?.(task);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.(task);
    }
  };

  return (
    <Card
      data-testid="task-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { elevation: 2 } : undefined }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="h6" component="h3" sx={{ fontWeight: 500, flexGrow: 1 }}>
              {task.title}
            </Typography>
            <Stack direction="row" spacing={1} flexShrink={0}>
              <TaskStatusChip status={task.status} />
              <TaskPriorityChip priority={task.priority} />
            </Stack>
          </Stack>
          {task.description ? (
            <Typography variant="body2" color="text.secondary">
              {task.description}
            </Typography>
          ) : null}
          {task.dueAt ? (
            <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
              Срок: {format(new Date(task.dueAt), 'd MMMM yyyy', { locale: ru })}
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
